/**
 * StationCard Component
 * Displays a single station row with its current status and actions
 */

import React, { useState } from 'react';
import { 
  Play, Pause, Trash2, Download, Users, Clock, Music, 
  ChevronDown, ChevronUp, Edit2, AlertTriangle, List,
  Headphones, Radio, Info, BarChart3, Stethoscope, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import PeriodSelector from './PeriodSelector';
import SongTable from './SongTable';
import EditStationForm from './EditStationForm';
// v2.9.203 — `NewEntriesTable` removed: the "Νέες Προσθήκες" tab was
// dropped from both Stream Monitor and Songs Analyzer at operator
// request (showed hundreds of songs without a day-control filter).
// The component file is left on disk for git history; nothing
// references it anymore.
import PlaylistChartView from './PlaylistChartView';
import StationHealthCheck from './StationHealthCheck';
import { formatDuration, getStatusIndicatorColor, getStatusIndicatorTitle } from './utils';
import { useRadioPlayer } from '../../contexts/RadioPlayerContext';
import { ShazamBadge } from './ShazamBadge';
import AudienceReachCard from '../AudienceReachCard';

// Calibration Offset Badge Component
const CalibrationBadge = ({ calibration }) => {
  if (!calibration) {
    return (
      <span 
        className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-200 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400 cursor-help"
        title="Αναμονή calibration — θα ενεργοποιηθεί αυτόματα"
        data-testid="calibration-badge-pending"
      >
        ⏱ —
      </span>
    );
  }

  const isRejected = calibration.confidence === 'rejected';
  const offset = calibration.offset_seconds || 0;
  const lastCal = calibration.last_calibrated 
    ? new Date(calibration.last_calibrated).toLocaleString('el-GR', {day:'numeric', month:'numeric', year:'2-digit', hour:'2-digit', minute:'2-digit'})
    : '—';
  
  if (isRejected || offset === 0) {
    return (
      <span 
        className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 cursor-help"
        title={`Δεν χρειάζεται διόρθωση — οι χρόνοι είναι ήδη ακριβείς\nMAE: ${calibration.mae || '—'}s\nΕπανέλεγχος κάθε 12 ώρες\nΤελευταίο: ${lastCal}`}
        data-testid="calibration-badge-rejected"
      >
        ⏱ Off
      </span>
    );
  }

  const accuracyColors = {
    excellent: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
    acceptable: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-400',
    needs_improvement: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  };
  const colorClass = accuracyColors[calibration.accuracy] || accuracyColors.acceptable;
  
  // Human-readable source names
  const sourceNames = {
    'duration_cluster_blend': 'Διάρκεια + Cross-Station',
    'duration_pattern': 'Ανάλυση Διάρκειας',
    'cross_station_cluster': 'Cross-Station Clustering',
    'fp_cluster_blend': 'Fingerprint + Cross-Station',
    'fingerprint': 'Audio Fingerprint',
  };
  const sourceName = sourceNames[calibration.source] || calibration.source;
  
  // Offset quality description
  const offsetDesc = offset < 30 ? '(χαμηλό — πολύ ακριβής)' 
    : offset < 90 ? '(μέτριο)' 
    : '(υψηλό — μεγάλη καθυστέρηση metadata)';
  
  // MAE quality description
  const maeVal = calibration.mae;
  const maeDesc = !maeVal ? '' 
    : maeVal < 60 ? ' (εξαιρετικό)' 
    : maeVal < 120 ? ' (αποδεκτό)' 
    : ' (χρειάζεται βελτίωση)';
  
  const tooltipText = [
    '⏱ Timestamp Calibration',
    `Offset: ${offset.toFixed(1)}s ${offsetDesc}`,
    `Πηγή: ${sourceName}`,
    `MAE: ${maeVal ? maeVal + 's' + maeDesc : '—'}`,
    `Τελευταίο: ${lastCal}`,
  ].join('\n');
  
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium cursor-help ${colorClass}`}
      title={tooltipText}
      data-testid="calibration-badge"
    >
      ⏱ {offset.toFixed(0)}s
    </span>
  );
};

// Data Mode Badge Component with tooltip
const DataModeBadge = ({ dataMode }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const isLive = dataMode === 'live';
  
  const tooltipText = isLive 
    ? 'Τα metrics βασίζονται σε πραγματικούς concurrent listeners από τον streaming server του σταθμού.\nΗ ανάλυση αποτυπώνει την πραγματική αντίδραση του κοινού σε κάθε τραγούδι.'
    : 'Τα metrics βασίζονται σε ανάλυση σταθερότητας και συμπεριφοράς του stream.\nΔεν περιλαμβάνονται πραγματικοί concurrent listeners.';
  
  return (
    <div 
      className="relative inline-flex items-center gap-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip(!showTooltip)}
    >
      <span 
        className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
          isLive 
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' 
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
        }`}
      >
        {isLive ? 'Live Audience' : 'Stream Analytics'}
      </span>
      <Info className="w-3 h-3 text-gray-400 cursor-help" />
      
      {/* Tooltip */}
      {showTooltip && (
        <div 
          className="absolute z-50 bottom-full left-0 mb-2 w-64 p-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg whitespace-pre-line"
          style={{ minWidth: '250px' }}
        >
          <div className={`font-semibold mb-1 ${isLive ? 'text-green-400' : 'text-blue-400'}`}>
            {isLive ? '📡 Live Audience' : '📊 Stream Analytics'}
          </div>
          {tooltipText}
          <div className="absolute bottom-0 left-4 transform translate-y-full">
            <div className="border-8 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// v2.9.54 (2026-05-29) — Polling Health Badge
// v2.9.63 (2026-05-29) — ALWAYS-visible color-coded indicator.
//
// Per user request: "δεν βλέπω σε όλους τους σταθμούς το miss%". The
// previous version only rendered when miss > 10%, which made healthy
// stations invisible. The operator wants the metric visible for EVERY
// Live Audience station so they can confirm at a glance which ones
// are working. Color coding now drives severity:
//   • ≥ 30 % miss → red
//   • 10-30 % miss → amber
//   • <  10 % miss → green ("✓ X% miss" — confirms it's monitored)
// Stream Analytics stations remain silent (the metric does not apply).
const PollingHealthBadge = ({ pollingHealth, dataMode }) => {
  if (!pollingHealth) return null;
  // Stream Analytics mode does not poll listeners externally — the
  // miss metric does not apply.
  if (dataMode === 'stream') return null;
  const noSamplesPct = pollingHealth.no_samples_pct ?? 0;
  const zeroOnlyPct = pollingHealth.zero_only_pct ?? 0;
  const lowSamplesPct = pollingHealth.low_samples_pct ?? 0;
  const goodSamplesPct = pollingHealth.good_samples_pct ?? 0;
  const totalPlays = pollingHealth.total_plays ?? 0;
  const avgSamples = pollingHealth.avg_samples ?? 0;

  // Don't show badge when there's no data yet (station too new, or
  // outside its schedule window so the filtered metric is empty).
  if (totalPlays === 0) return null;

  // v2.9.63 — Three-tier severity coding.
  let colorClass;
  let icon;
  let prefix;
  if (noSamplesPct >= 30) {
    colorClass = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-300/40';
    icon = '⚠️';
    prefix = 'Listeners';
  } else if (noSamplesPct >= 10) {
    colorClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ring-1 ring-amber-300/40';
    icon = '⚠️';
    prefix = 'Listeners';
  } else {
    colorClass = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 ring-1 ring-green-300/40';
    icon = '✓';
    prefix = 'Listeners';
  }

  // Reason hints — what URLs are missing.
  const missingUrls = [];
  if (pollingHealth.has_listeners_api_url === false) missingUrls.push('listeners_api_url');
  if (pollingHealth.has_stats_url === false) missingUrls.push('stats_url');
  if (pollingHealth.has_orb_id === false) missingUrls.push('onlineradiobox_id');
  const allMissing = missingUrls.length === 3;

  const healthLine = noSamplesPct < 10
    ? '✅ Υγιής — listeners συλλέγονται κανονικά'
    : noSamplesPct < 30
      ? '🟠 Χρειάζεται προσοχή'
      : '🔴 Πρόβλημα συλλογής listeners';

  const tooltip = (
    `${healthLine}  (τελευταίο 24ωρο, εντός schedule)\n\n` +
    `• Συνολικά plays ≥120s : ${totalPlays}\n` +
    `• Χωρίς δείγματα       : ${noSamplesPct.toFixed(1)}%\n` +
    `• Με μόνο μηδενικά     : ${zeroOnlyPct.toFixed(1)}%\n` +
    `• Λίγα δείγματα (1-2)  : ${lowSamplesPct.toFixed(1)}%\n` +
    `• Καλά (≥3 samples)    : ${goodSamplesPct.toFixed(1)}%\n` +
    `• Μέσος όρος samples   : ${avgSamples.toFixed(2)}\n\n` +
    (allMissing
      ? `Καμία πηγή listener data δεν είναι ρυθμισμένη.\n`
        + `Πρόσθεσε ένα από:\n`
        + `  • listeners_api_url\n`
        + `  • stats_url\n`
        + `  • onlineradiobox_id`
      : missingUrls.length
        ? `Δεν έχει: ${missingUrls.join(', ')}`
        : noSamplesPct >= 10
          ? `Όλες οι πηγές υπάρχουν — έλεγξε τη διαθεσιμότητα του endpoint.`
          : `Όλες οι πηγές υπάρχουν και απαντούν σωστά.`)
  );

  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium cursor-help inline-flex items-center gap-1 ${colorClass}`}
      title={tooltip}
      data-testid="polling-health-badge"
    >
      <span className="leading-none">{icon}</span>
      {prefix} {noSamplesPct.toFixed(0)}% miss
    </span>
  );
};

// v2.13.52 (2026-06-22) — Per-Station Songs Health Badge.
// Operator request: at-a-glance per-station colour matching the
// global pipeline thresholds, plus song counts for 5m / 1h / 24h.
//   • GREEN  songs_1h ≥ 5
//   • YELLOW songs_1h 1-4
//   • RED    songs_1h = 0 inside recording window with last_song < 24h
//   • BLUE   off-schedule / never recorded / long offline
// NO new calculation — backend computes the same way as the
// yellow-stuck-report so the colours align with what the operator
// already trusts.

// 2026-07-17 — Recording Coverage Badge.
// Compact per-station indicator answering "Did we record what we
// should have in the last hour?". Uses the 7-day baseline scaled to
// 1h from the backend `recording_coverage` payload. State thresholds
// align with recording_truth_analyzer (healthy ≥70%, warning ≥40%).
const RecordingCoverageBadge = ({ coverage }) => {
  if (!coverage) return null;
  const {
    coverage_pct: cov,
    state,
    expected_1h,
    songs_1h,
    songs_7d,
    in_window,
    schedule,
    scheduled_hours_per_day: schedHpd,
  } = coverage;

  const styles = {
    healthy: {
      cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 ring-1 ring-emerald-300/40',
      dot: 'bg-emerald-500',
      label: 'Υγιής κάλυψη',
    },
    warning: {
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ring-1 ring-amber-300/40',
      dot: 'bg-amber-500',
      label: 'Χαμηλή κάλυψη',
    },
    critical: {
      cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-300/40',
      dot: 'bg-red-500',
      label: 'Κρίσιμη κάλυψη',
    },
    unknown: {
      cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400 ring-1 ring-slate-300/30',
      dot: 'bg-slate-400',
      label: (in_window === false)
        ? 'Εκτός προγράμματος'
        : 'Ανεπαρκή δεδομένα',
    },
  };
  const s = styles[state] || styles.unknown;

  const covLabel = cov == null ? '—' : `${Math.round(cov)}%`;
  const scheduleStr = schedule || (schedHpd != null
    ? `${schedHpd}h/ημέρα`
    : 'άγνωστο');
  const tooltip =
    `${s.label}\n\n` +
    `• Κάλυψη 1h  : ${cov == null ? 'Δεν διαθέσιμη' : `${cov}%`} ` +
    `(cap 100%)\n` +
    `• Αναμενόμενα: ${expected_1h ?? '—'} τραγ./ώρα ` +
    `(baseline 7 ημερών, ΜΟΝΟ ώρες προγράμματος)\n` +
    `• Πρόγραμμα  : ${scheduleStr}\n` +
    `• Καταγραφή  : ${songs_1h ?? 0} τελ. 1h · ${songs_7d ?? 0} τελ. 7d\n` +
    `• Σε πρόγραμμα τώρα: ${in_window ? 'ναι' : 'όχι'}\n\n` +
    `Όρια: 🟢 ≥70%   🟡 40-69%   🔴 <40%   ⚪ ανεπαρκή/εκτός`;

  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium cursor-help inline-flex items-center gap-1.5 ${s.cls}`}
      title={tooltip}
      data-testid={`recording-coverage-badge-${state}`}
    >
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      <span className="font-semibold">Cov</span>
      <span>{covLabel}</span>
    </span>
  );
};

const SongsHealthBadge = ({ songsHealth }) => {
  if (!songsHealth) return null;
  const { songs_5m: c5m, songs_1h: c1h, songs_24h: c24h, status, last_song_age_seconds: lastAge } = songsHealth;

  // Colour + dot per status.
  const styles = {
    GREEN: {
      cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 ring-1 ring-green-300/40',
      dot: 'bg-green-500',
      label: 'Υγιής',
    },
    YELLOW: {
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ring-1 ring-amber-300/40',
      dot: 'bg-amber-500',
      label: 'Χαμηλή ροή',
    },
    RED: {
      cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ring-1 ring-red-300/40',
      dot: 'bg-red-500',
      label: 'Κολλημένος',
    },
    BLUE: {
      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 ring-1 ring-blue-300/40',
      dot: 'bg-blue-500',
      label: 'Εκτός / Ανενεργός',
    },
  };
  const s = styles[status] || styles.BLUE;

  const lastSongStr = lastAge == null
    ? '—'
    : lastAge < 60
      ? `${lastAge}s πριν`
      : lastAge < 3600
        ? `${Math.floor(lastAge / 60)}m πριν`
        : `${Math.floor(lastAge / 3600)}h πριν`;

  const tooltip =
    `${s.label} — βάσει τραγουδιών/ώρα\n\n` +
    `• 5 λεπτά  : ${c5m}\n` +
    `• 1 ώρα    : ${c1h}\n` +
    `• 24 ώρες  : ${c24h}\n` +
    `• Τελευταίο: ${lastSongStr}\n\n` +
    `Όρια (ίδια με γενικό pipeline):\n` +
    `  🟢 ≥5 / ώρα   🟡 1-4 / ώρα\n` +
    `  🔴 0 σε ώρες λειτουργίας   🔵 εκτός / νέος`;

  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-medium cursor-help inline-flex items-center gap-1.5 ${s.cls}`}
      title={tooltip}
      data-testid={`songs-health-badge-${status.toLowerCase()}`}
    >
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      <span className="font-semibold">Songs</span>
      <span className="opacity-80">{`5':${c5m}`}</span>
      <span className="opacity-80">{`·1h:${c1h}`}</span>
      <span className="opacity-80">{`·24h:${c24h}`}</span>
    </span>
  );
};

const StationCard = ({
  station,
  realtime,
  pollingHealth,
  songsHealth,
  isExpanded,
  isEditing,
  stats,
  statsLoading,
  period,
  token,
  apiUrl,
  sortConfig,
  onSortChange,
  // Edit form props
  editForm,
  setEditForm,
  editTestResult,
  editTesting,
  // Callbacks
  onToggleExpand,
  onChangePeriod,
  onToggleStation,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onTestEditUrl,
  onFingerprintEdit,
  onTestMetadata,
  metadataDiagnostic,
  onClearDiagnostic,
  onDelete,
  onExportXlsx,
  exportingStationId
}) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [showPlaylistChart, setShowPlaylistChart] = useState(false);
  const [showHealthCheck, setShowHealthCheck] = useState(false);
  const { currentStation, isPlaying, isLoading, playStation } = useRadioPlayer();
  const nowPlaying = realtime?.now_playing;
  const hasGap = realtime?.gap;
  
  // Check if this station is currently playing
  const isThisStationPlaying = currentStation?._id === station._id && isPlaying;
  const isThisStationLoading = currentStation?._id === station._id && isLoading;

  return (
    <div data-station-id={station._id} className="bg-white dark:bg-gray-800/60 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Gap Alert */}
      {hasGap && (
        <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 border-b border-yellow-500/30">
          <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
          <span className="text-xs text-yellow-700 dark:text-yellow-400">
            ⚠️ Διακοπή καταγραφής: {new Date(hasGap.stop_time).toLocaleTimeString('el-GR')} 
            {' '}(διάρκεια: {hasGap.duration_formatted})
            {hasGap.reason && ` - ${hasGap.reason}`}
          </span>
        </div>
      )}
      
      {/* Edit Form */}
      {isEditing ? (
        <EditStationForm
          station={station}
          editForm={editForm}
          setEditForm={setEditForm}
          editTestResult={editTestResult}
          editTesting={editTesting}
          onTestUrl={onTestEditUrl}
          onFingerprint={onFingerprintEdit}
          onTestMetadata={onTestMetadata}
          metadataDiagnostic={metadataDiagnostic}
          onClearDiagnostic={onClearDiagnostic}
          onSave={() => onSaveEdit(station._id)}
          onCancel={onCancelEdit}
          apiUrl={apiUrl}
          token={token}
        />
      ) : (
        <>
          {/* Station Row — wrapped in flex-col so now-playing appears on its own second line */}
          <div className="flex flex-col">
          {/*
            v2.9.71 (2026-06-01) — Bug fix: expand chevron disappeared on
            stations with many inline metric badges (Live Audience +
            Listeners 0% miss + ⚠ + U:/LT:/C:). The previous
            `sm:flex-nowrap` forced everything onto a single line; when
            the cumulative width exceeded the card width, the parent's
            `overflow-hidden` (needed for rounded corners) silently
            clipped the trailing controls — Download, Chart, and the
            expand `v` — making the dropdown unreachable.

            Switched to plain `flex-wrap` at every breakpoint so the
            actions block falls to a second visual line when it can't
            fit; the chevron is now ALWAYS reachable. Visual ordering
            is preserved, only line-break behavior changes.
          */}
          <div className="flex flex-wrap items-center gap-2 px-2 py-3 sm:px-3 hover:bg-gray-100 dark:hover:bg-gray-700/30 transition-colors">
            {/* First row on mobile: Status, Listen, Name, Expand button */}
            <div className="flex flex-col gap-0.5 w-full sm:w-auto sm:flex-shrink-0">
              {/* Top row: Status, Listen button, Name */}
              <div className="flex items-center gap-2">
                {/* Status indicator */}
                <div 
                  className={`w-3 h-3 rounded-full flex-shrink-0 shadow-md ${getStatusIndicatorColor(station, realtime)}`}
                  title={getStatusIndicatorTitle(station, realtime)}
                />
                
                {/* Listen Button - Only show if stream_url exists */}
                {station.stream_url && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      playStation(station);
                    }}
                    size="sm"
                    variant="ghost"
                    className={`h-7 w-7 p-0 rounded-full flex-shrink-0 transition-all ${
                      isThisStationPlaying 
                        ? 'bg-green-500 text-white hover:bg-green-600 animate-pulse' 
                        : isThisStationLoading
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                    }`}
                    title={isThisStationPlaying ? 'Παίζει τώρα - Κλικ για παύση' : 'Ακρόαση σταθμού'}
                  >
                    {isThisStationLoading ? (
                      <Radio className="w-3.5 h-3.5 animate-spin" />
                    ) : isThisStationPlaying ? (
                      <Headphones className="w-3.5 h-3.5" />
                    ) : (
                      <Headphones className="w-3.5 h-3.5" />
                    )}
                  </Button>
                )}
                
                {/* Manual Override indicator */}
                {realtime?.manual_override && (
                  <span className="text-green-500 text-base font-bold" title="Χειροκίνητο override">⚡</span>
                )}
                
                {/* Schedule indicator */}
                {station.schedule_enabled && !realtime?.manual_override && (
                  <span 
                    className="text-base text-blue-500 dark:text-blue-400" 
                    title={`Πρόγραμμα: ${station.schedule_start_time || '06:00'}-${station.schedule_end_time || '20:00'} (Ώρα Ελλάδας)`}
                  >
                    🕐
                  </span>
                )}
                
                {/* Threshold Suggestion Warning */}
                {station.listener_thresholds_suggested && (
                  station.listener_thresholds_suggested.high !== station.listener_threshold_high ||
                  station.listener_thresholds_suggested.medium !== station.listener_threshold_medium ||
                  station.listener_thresholds_suggested.low !== station.listener_threshold_low
                ) && (
                  <span 
                    className="text-amber-500 text-sm" 
                    title="Νέες προτεινόμενες τιμές κατωφλίων διαθέσιμες"
                  >
                    ⚠️
                  </span>
                )}
                
                {/* Name - Increased width */}
                <span 
                  className="font-medium text-sm w-24 sm:w-32 truncate flex-shrink-0 text-gray-900 dark:text-white" 
                  title={station.name}
                >
                  {station.name}
                </span>

                {/* v2.9.150 — Shazam badge: blue=mandatory, gray=fallback.
                    `shazamMode` is computed by the parent via useShazamModeMap. */}
                {station.shazam_mode && (
                  <ShazamBadge mode={station.shazam_mode} size="sm" />
                )}
                
                {/* Expand Button - VISIBLE ON MOBILE (moved here for mobile view) */}
                <Button 
                  onClick={() => onToggleExpand(station._id)} 
                  size="sm" 
                  variant="ghost" 
                  className="h-7 w-7 p-0 sm:hidden ml-auto bg-blue-500/20 hover:bg-blue-500/40"
                  title="Προβολή τραγουδιών"
                >
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Bottom row: Data Mode Badge + Calibration Badge + Polling Health Badge */}
              <div className="pl-5 sm:pl-12 flex items-center gap-1.5 flex-wrap">
                <DataModeBadge dataMode={station.data_mode || 'stream'} />
                <CalibrationBadge calibration={realtime?.calibration} />
                <PollingHealthBadge pollingHealth={pollingHealth} dataMode={station.data_mode || 'stream'} />
              </div>
              {/* v2.13.52 — Per-station Songs Health row.
                  Separate line so the 3-window counts have room to
                  breathe without crowding the polling-health badge. */}
              {songsHealth && (
                <div className="pl-5 sm:pl-12 flex items-center gap-1.5 flex-wrap">
                  <SongsHealthBadge songsHealth={songsHealth} />
                  {/* 2026-07-17 — Recording Coverage badge.
                      Answers "Did the station record what it should
                      have in the last hour?" independently of the
                      Songs pipeline colour. */}
                  <RecordingCoverageBadge coverage={songsHealth?.recording_coverage} />
                </div>
              )}
            </div>
            
            {/* Now Playing moved to its own second line below (see after this row) */}

            {/*
              v2.13.65 (2026-06-25) — Mobile overlap fix.
              Previously each metric (Listeners, Today's Stats, Duration)
              lived as a sibling of the name block + actions, and on
              phones they wrapped into a tight row where the empty
              Today's Stats placeholder "—" left no margin between the
              Users (👥) and Clock (🕐) icons, causing visual overlap
              ("0Я. 1") on stations whose provider doesn't expose
              extended stats.
              The 3 chips are now grouped in a single flex-wrap sub-
              container with `gap-3` so they always breathe apart, the
              empty placeholder is no longer rendered, and each chip
              carries `whitespace-nowrap` so icon+number never split.
            */}
            <div className="flex items-center gap-3 flex-wrap min-w-0 w-full sm:w-auto sm:flex-1">
              {/* Listeners - Show real listeners for all modes. */}
              <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 flex-shrink-0 whitespace-nowrap" title="Ταυτόχρονοι ακροατές τώρα">
                <Users className="w-3 h-3" />
                {(nowPlaying?.listeners > 0)
                  ? nowPlaying.listeners
                  : (realtime?.station_listeners != null && realtime?.station_listeners >= 0)
                    ? <span className={realtime.station_listeners > 0 ? "text-blue-500" : "text-gray-500"} title="Ταυτόχρονοι ακροατές τώρα">{realtime.station_listeners}</span>
                    : (realtime?.station_listeners_today_avg != null && realtime.station_listeners_today_avg > 0)
                      ? <span className="text-gray-500 italic" title={`Μέσος όρος σήμερα: ${realtime.station_listeners_today_avg} (χωρίς live δείγμα αυτή τη στιγμή)`}>~{Math.round(realtime.station_listeners_today_avg)}</span>
                      : <span className="text-gray-400">—</span>
                }
              </div>

              {/* Today's Listener Stats (Peak/Avg/TLH/U/LT/C) — rendered
                  only when the provider exposes them, so we never leak
                  an empty "—" placeholder that collapses the spacing
                  between Listeners and Duration. */}
              {realtime?.listener_stats_today && (
                <div
                  className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 min-w-0 lg:max-w-[260px] flex-wrap"
                  title={`Σήμερα: Peak ${realtime.listener_stats_today.peak}, Μ.Ο. ${realtime.listener_stats_today.avg}, TLH ${realtime.listener_stats_today.tlh}`}
                >
                  <span className="text-orange-500 whitespace-nowrap">P:{realtime.listener_stats_today.peak}</span>
                  <span className="text-cyan-500 whitespace-nowrap">Μ:{realtime.listener_stats_today.avg}</span>
                  <span className="text-purple-500 whitespace-nowrap">TLH:{realtime.listener_stats_today.tlh}</span>
                  {/* v2026-04-21: Extended signal fields — shown only when provider reports them */}
                  {realtime.listener_stats_today.unique_peak != null && (
                    <span className="text-emerald-500 whitespace-nowrap" title={`Unique IPs today: peak ${realtime.listener_stats_today.unique_peak}, avg ${realtime.listener_stats_today.unique_avg ?? '-'}`}>
                      U:{realtime.listener_stats_today.unique_peak}
                    </span>
                  )}
                  {realtime.listener_stats_today.avg_session_seconds != null && (
                    <span className="text-amber-500 whitespace-nowrap" title="Μέσος χρόνος ακρόασης (από provider)">
                      LT:{(() => {
                        const s = realtime.listener_stats_today.avg_session_seconds;
                        if (s < 60) return `${s}s`;
                        if (s < 3600) return `${Math.floor(s / 60)}m`;
                        const h = Math.floor(s / 3600);
                        return `${h}h`;
                      })()}
                    </span>
                  )}
                  {realtime.listener_stats_today.server_max != null && realtime.listener_stats_today.peak > 0 && (
                    <span className="text-rose-500 whitespace-nowrap" title={`Server capacity: max ${realtime.listener_stats_today.server_max} listeners`}>
                      C:{Math.round(100 * realtime.listener_stats_today.peak / realtime.listener_stats_today.server_max)}%
                    </span>
                  )}
                </div>
              )}

              {/* Duration */}
              <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 flex-shrink-0 whitespace-nowrap" title="Διάρκεια αναπαραγωγής τρέχοντος τραγουδιού">
                <Clock className="w-3 h-3" />
                {formatDuration(nowPlaying?.current_duration_seconds)}
              </div>
            </div>
            
            {/* Actions — v2.9.72: switched to `flex-1 justify-end` so the
                entire actions cluster (play/edit/diagnostic/trash/period/
                download/chart/expand) is RIGHT-ALIGNED even when the row
                wraps onto a second line. Previously the wrap produced a
                left-aligned actions strip on overflowed cards, which the
                user found visually inconsistent and harder to scan. */}
            <div className="flex items-center gap-1 flex-1 justify-end min-w-0">
              {/* Play/Pause Button */}
              <Button 
                onClick={() => onToggleStation(station._id, station.name, station.active)} 
                size="sm" 
                variant="ghost" 
                className={`h-7 px-2 ${station.active ? 'text-green-600 dark:text-green-400 hover:text-yellow-500' : 'text-gray-500 hover:text-green-500'}`}
                title={station.active ? 'Παύση σταθμού' : 'Ενεργοποίηση σταθμού'}
              >
                {station.active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              </Button>
              
              <Button 
                onClick={() => onStartEdit(station)} 
                size="sm" 
                variant="ghost" 
                className="h-7 px-2 hidden sm:flex" 
                title="Επεξεργασία"
              >
                <Edit2 className="w-3 h-3" />
              </Button>

              <Button 
                onClick={() => setShowHealthCheck(true)} 
                size="sm" 
                variant="ghost" 
                className="h-7 px-2 hidden sm:flex text-cyan-600 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/30" 
                title="Station Diagnostic"
                data-testid={`health-check-btn-${station._id}`}
              >
                <Stethoscope className="w-3 h-3" />
              </Button>
              
              <Button 
                onClick={() => onDelete(station._id, station.name)} 
                size="sm" 
                variant="ghost" 
                className="h-7 px-2 text-red-600 dark:text-red-400 hidden sm:flex" 
                title="Διαγραφή"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
              
              {/* Period selector with date range support */}
              <PeriodSelector
                value={period}
                onChange={(newPeriod) => onChangePeriod(station._id, newPeriod)}
                customDateRange={station.customDateRange}
                onCustomDateChange={(start, end) => {
                  // Store custom date range in station state
                  if (typeof onChangePeriod === 'function') {
                    onChangePeriod(station._id, `custom_${start}_${end}`, { start, end });
                  }
                }}
              />
              
              <Button 
                onClick={() => onExportXlsx(station._id, station.name)} 
                size="sm" 
                variant="ghost" 
                className="h-7 px-2 hidden sm:flex" 
                title={exportingStationId === station._id ? "Δημιουργία αρχείου..." : "Εξαγωγή Excel"}
                disabled={exportingStationId === station._id}
              >
                {exportingStationId === station._id 
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Download className="w-3 h-3" />
                }
              </Button>
              
              {/* Playlist Chart Button */}
              <Button 
                onClick={() => setShowPlaylistChart(true)} 
                size="sm" 
                variant="ghost" 
                className="h-7 px-2 hidden sm:flex text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30" 
                title="Playlist Chart (Radiomitor-style)"
              >
                <BarChart3 className="w-3 h-3" />
              </Button>
              
              {/* Expand Button - Desktop only (mobile version is above)
                  v2.9.71/72: flex-shrink-0 + light tint so this control is
                  always reachable and visually identifiable. Right-
                  alignment is handled by the parent actions container
                  (flex-1 justify-end). */}
              <Button 
                onClick={() => onToggleExpand(station._id)} 
                size="sm" 
                variant="ghost" 
                className="h-7 px-2 hidden sm:flex flex-shrink-0 bg-blue-500/10 hover:bg-blue-500/30"
                title="Προβολή λεπτομερειών σταθμού"
                data-testid={`expand-btn-${station._id}`}
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Second line: Now Playing (horizontal, truncated to fit width) */}
          <div className="flex items-center gap-2 px-3 pb-2 pl-8 sm:pl-[4.5rem] min-w-0" data-testid={`now-playing-${station._id}`}>
            <Music className="w-3 h-3 text-blue-500 dark:text-blue-400 flex-shrink-0" />
            <span
              className="text-sm text-gray-700 dark:text-gray-300 truncate min-w-0 flex-1"
              title={nowPlaying?.full_title}
            >
              {nowPlaying?.full_title || 'Αναμονή...'}
            </span>
            {nowPlaying?.fingerprint_used && (
              <span className="text-xs text-purple-600 dark:text-purple-400 flex-shrink-0" title="Αναγνωρίστηκε με fingerprint">🎤</span>
            )}
          </div>
          </div>
          
          {/* Expanded Stats */}
          {isExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              {/* v2.9.172 — Audience Reach Estimate (always visible on expand) */}
              <div className="p-3 pb-0">
                <AudienceReachCard
                  stationId={station._id}
                  token={token}
                  variant="light"
                  data-testid={`audience-reach-${station._id}`}
                />
              </div>
              {/* Tabs */}
              <div className="flex items-center gap-1 px-3 pt-2 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab('stats')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
                    activeTab === 'stats' 
                      ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-b-0 border-gray-200 dark:border-gray-700' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <List className="w-3 h-3" />
                  Στατιστικά
                </button>
                {/* v2.9.203 — "Νέες Προσθήκες" tab removed at operator
                    request. Same reason as in SongsAnalyzerPage. */}
              </div>
              
              {/* Tab Content */}
              <div className="p-3">
                {activeTab === 'stats' && (
                  <SongTable 
                    stats={stats} 
                    loading={statsLoading} 
                    sortConfig={sortConfig}
                    onSortChange={onSortChange}
                    listenerThresholds={station.listener_thresholds}
                    stationId={station._id}
                    period={period}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Playlist Chart Modal */}
      {showPlaylistChart && (
        <PlaylistChartView
          stationId={station._id}
          stationName={station.name}
          onClose={() => setShowPlaylistChart(false)}
        />
      )}

      {/* Station Health Check Modal */}
      <StationHealthCheck
        stationId={station._id}
        stationName={station.name}
        open={showHealthCheck}
        onClose={() => setShowHealthCheck(false)}
        apiUrl={apiUrl}
        token={token}
      />
    </div>
  );
};

export default StationCard;
